import { addDays, addHours, addMinutes } from "date-fns";
import * as Yup from "yup";

// SCHEMA USED IN ADD NEW CARGO, ADD SIMILAR CARGO AND EDIT CARGO
export const getFormSchema = (t) => {
	return Yup.object().shape({
		loadingAddressCountry: Yup.string().when(
			[
				"loadingAddressCustomPostalCode",
				"loadingAddressCustomCity",
				"loadingAddressCustomCountry",
			],
			(
				[
					loadingAddressCustomPostalCode,
					loadingAddressCustomCity,
					loadingAddressCustomCountry,
				],
				schema,
			) => {
				const allCustomFieldsFilled =
					loadingAddressCustomPostalCode &&
					loadingAddressCustomCity &&
					loadingAddressCustomCountry;

				return allCustomFieldsFilled
					? schema.nullable().notRequired()
					: schema.required(t("fill_required_fields"));
			},
		),
		loadingAddress: Yup.object().when(
			[
				"loadingAddressCustomPostalCode",
				"loadingAddressCustomCity",
				"loadingAddressCustomCountry",
			],
			(
				[
					loadingAddressCustomPostalCode,
					loadingAddressCustomCity,
					loadingAddressCustomCountry,
				],
				schema,
			) => {
				const allCustomFieldsFilled =
					loadingAddressCustomPostalCode &&
					loadingAddressCustomCity &&
					loadingAddressCustomCountry;

				return allCustomFieldsFilled
					? schema.notRequired().nullable()
					: schema
							.typeError(t("fill_required_fields"))
							.required(t("fill_required_fields"));
			},
		),
		loadingAddressCustomPostalCode: Yup.string(),
		loadingAddressCustomCity: Yup.string(),
		loadingAddressCustomCountry: Yup.string(),
		loadingDate: Yup.mixed().when(
			"loadingDateType",
			([loadingDateType], schema) => {
				return loadingDateType === "day"
					? Yup.date()
							.required(t("fill_required_fields"))
							.typeError(t("invalid_date_format"))
							.min(
								new Date(new Date().setHours(0, 0, 0, 0)),
								t("date_cant_be_from_past"),
							)
					: schema.notRequired().nullable();
			},
		),
		loadingRepeat: Yup.string(),
		unloadingAddressCountry: Yup.string().when(
			[
				"unloadingAddressCustomPostalCode",
				"unloadingAddressCustomCity",
				"unloadingAddressCustomCountry",
			],
			(
				[
					unloadingAddressCustomPostalCode,
					unloadingAddressCustomCity,
					unloadingAddressCustomCountry,
				],
				schema,
			) => {
				const allCustomFieldsFilled =
					unloadingAddressCustomPostalCode &&
					unloadingAddressCustomCity &&
					unloadingAddressCustomCountry;

				return allCustomFieldsFilled
					? schema.nullable().notRequired()
					: schema.required(t("fill_required_fields"));
			},
		),
		unloadingAddress: Yup.object().when(
			[
				"unloadingAddressCustomPostalCode",
				"unloadingAddressCustomCity",
				"unloadingAddressCustomCountry",
			],
			(
				[
					unloadingAddressCustomPostalCode,
					unloadingAddressCustomCity,
					unloadingAddressCustomCountry,
				],
				schema,
			) => {
				const allCustomFieldsFilled =
					unloadingAddressCustomPostalCode &&
					unloadingAddressCustomCity &&
					unloadingAddressCustomCountry;

				return allCustomFieldsFilled
					? schema.notRequired().nullable()
					: schema
							.typeError(t("fill_required_fields"))
							.required(t("fill_required_fields"));
			},
		),
		unloadingAddressCustomPostalCode: Yup.string(),
		unloadingAddressCustomCity: Yup.string(),
		unloadingAddressCustomCountry: Yup.string(),
		unloadingDate: Yup.mixed().when(
			"unloadingDateType",
			([unloadingDateType], schema) => {
				return unloadingDateType === "day"
					? Yup.date()
							.required(t("fill_required_fields"))
							.typeError(t("invalid_date_format"))
							.min(
								new Date(new Date().setHours(0, 0, 0, 0)),
								t("date_cant_be_from_past"),
							)
							.when(
								["loadingDate", "loadingDateType"],
								([loadingDate, loadingDateType], schema) => {
									if (loadingDate && loadingDateType === "day") {
										if (
											!(loadingDate instanceof Date) ||
											isNaN(loadingDate.getTime())
										) {
											return schema.test(() => false);
										}
										let minUnloadingDate = new Date(loadingDate);

										let maxUnloadingDate = new Date(loadingDate);

										// check if valid dates
										if (
											!(minUnloadingDate instanceof Date) ||
											isNaN(minUnloadingDate.getTime()) ||
											!(maxUnloadingDate instanceof Date) ||
											isNaN(maxUnloadingDate.getTime())
										) {
											return schema.test({
												name: "invalid-dates",
												exclusive: true,
												message: t("invalid_date_format"),
												test: () => false,
											});
										}

										minUnloadingDate.setHours(0, 0, 0, 0);

										maxUnloadingDate.setHours(0, 0, 0, 0);
										maxUnloadingDate = addDays(maxUnloadingDate, 30);

										return schema
											.min(
												minUnloadingDate,
												t("unloading_date_cant_be_before_loading_date"),
											)
											.max(
												maxUnloadingDate,
												t(
													"unloading_date_cant_be_later_than_loading_date_plus_30_days",
												),
											);
									}
									return schema;
								},
							)
							.when(
								["loadingDateType", "loadingDateRange"],
								([loadingDateType, loadingDateRange], schema) => {
									if (
										loadingDateType === "range" &&
										loadingDateRange &&
										loadingDateRange.length > 1
									) {
										const startLoadingRange = loadingDateRange[0];
										const endLoadingRange = loadingDateRange[1];
										let minUnloadingDate = new Date(endLoadingRange);

										let maxUnloadingDate = new Date(startLoadingRange);

										// check if valid dates
										if (
											!(minUnloadingDate instanceof Date) ||
											isNaN(minUnloadingDate.getTime()) ||
											!(maxUnloadingDate instanceof Date) ||
											isNaN(maxUnloadingDate.getTime())
										) {
											return schema.test({
												name: "invalid-dates",
												exclusive: true,
												message: t("invalid_date_format"),
												test: () => false,
											});
										}

										minUnloadingDate.setHours(0, 0, 0, 0);

										maxUnloadingDate.setHours(0, 0, 0, 0);
										maxUnloadingDate = addDays(maxUnloadingDate, 30);

										return schema
											.min(
												minUnloadingDate,
												t(
													"unloading_date_cant_be_before_second_date_of_loading",
												),
											)
											.max(
												maxUnloadingDate,
												t(
													"unloading_date_cant_be_later_than_first_date_of_loading_plus_30_days",
												),
											);
									}
								},
							)
					: schema.notRequired().nullable();
			},
		),
		weight: Yup.number()
			.required(t("fill_required_fields"))
			.transform((_, value) => {
				if (`${value}`.includes(".")) {
					return +value;
				}
				return +`${value}`.replace(/,/, ".");
			})
			.nullable(true)
			.typeError(t("value_has_to_be_number"))
			.min(0.0001, t("value_has_to_be_greater_than_zero"))
			.max(40, t("weight_has_to_be_less_than_40t")),
		length: Yup.number()
			.required(t("fill_required_fields"))
			.transform((_, value) => {
				if (`${value}`.includes(".")) {
					return +value;
				}
				return +`${value}`.replace(/,/, ".");
			})
			.nullable(true)
			.typeError(t("value_has_to_be_number"))
			.min(0.0001, t("value_has_to_be_greater_than_zero")),
		mailingToGroups: Yup.boolean()
			.test("checkboxes", t("sales_support_section_error"), function (_value) {
				const {
					mailingToGroups,
					shouldPostOnStock,
					autoSearchFreeCars,
					shouldBePublic,
				} = this.parent;

				return (
					mailingToGroups ||
					shouldPostOnStock ||
					autoSearchFreeCars ||
					shouldBePublic
				);
			})
			.test(
				"mailingGroups",
				t("select_at_least_one_mailing_group"),
				function (value) {
					const { mailingGroups_1, mailingGroups_2, mailingGroups_3 } =
						this.parent;
					// If mailingToGroups checkbox is checked, at least one mailingGroups must have a value
					return (
						!value || mailingGroups_1 || mailingGroups_2 || mailingGroups_3
					);
				},
			),
		mailingGroups_1: Yup.string(),
		mailingGroups_2: Yup.string(),
		mailingGroups_3: Yup.string(),
		shouldPostOnStock: Yup.boolean()
			.test("postOn", t("select_at_least_one_stock"), function (value) {
				const {
					postOnTrans,
					postOnTimocom,
					postOnTeleroute,
					postOnTransporeon,
					postOnComarr,
				} = this.parent;
				// If shouldPostOnStock checkbox is checked, at least one postOn must be checked
				return (
					!value ||
					postOnTrans ||
					postOnTimocom ||
					postOnTeleroute ||
					postOnTransporeon ||
					postOnComarr
				);
			})
			.test(
				"shouldPostAfter",
				t("select_time_after_should_publish_on_stocks"),
				function (value) {
					const {
						shouldPostAfter,
						postOnTrans,
						postOnTimocom,
						postOnTeleroute,
						postOnTransporeon,
						postOnComarr,
					} = this.parent;
					// If shouldPostOnStock checkbox is checked, shouldPostAfter must be selected (except for Trans)
					if (
						postOnTrans &&
						!postOnTimocom &&
						!postOnTeleroute &&
						!postOnTransporeon &&
						!postOnComarr
					) {
						return true;
					}

					return (
						!value ||
						(shouldPostAfter !== undefined && shouldPostAfter !== null)
					);
				},
			),
		postOnTrans: Yup.boolean(),
		postOnTimocom: Yup.boolean(),
		postOnTeleroute: Yup.boolean(),
		postOnTransporeon: Yup.boolean(),
		postOnComarr: Yup.boolean(),
		shouldPostAfter: Yup.number().nullable(),
		autoSearchFreeCars: Yup.boolean(),
		shouldBePublic: Yup.boolean(),
		vehicleTypeId: Yup.array()
			.min(1, t("fill_required_fields"))
			.required(t("fill_required_fields")),
		vehicleBodyId: Yup.array()
			.min(1, t("fill_required_fields"))
			.max(5, t("you_can_select_max_n_values", { max: 5 }))
			.required(t("fill_required_fields")),
		cargoTypeId: Yup.string()
			.nullable()
			.test(
				"is-cargoTypeId-required",
				t("fill_required_fields"),
				function (value) {
					const { postOnTrans } = this.parent;
					if (!value && postOnTrans) {
						return false;
					}
					return true;
				},
			),
		price: Yup.number()
			.notRequired()
			.typeError(t("value_has_to_be_number"))
			.nullable()
			.transform((value, originalValue) =>
				String(originalValue).trim() === "" ? null : value,
			)
			.min(1, t("value_has_to_be_greater_than_zero")),
		customerPrice: Yup.number()
			.notRequired()
			.typeError(t("value_has_to_be_number"))
			.nullable()
			.transform((value, originalValue) =>
				String(originalValue).trim() === "" ? null : value,
			)
			.min(1, t("value_has_to_be_greater_than_zero")),
		payment: Yup.number()
			.notRequired()
			.typeError(t("value_has_to_be_number"))
			.nullable()
			.transform((value, originalValue) =>
				String(originalValue).trim() === "" ? null : value,
			)
			.min(1, t("value_has_to_be_greater_than_zero")),

		loadingDateType: Yup.string().required(t("select_date_type_load")),
		loadingDateRange: Yup.array()
			.of(Yup.date())
			.nullable()
			.when("loadingDateType", ([loadingDateType], schema) => {
				if (loadingDateType === "range") {
					return schema
						.required(t("range_is_required_if_selected_range"))
						.test("is-valid-dates", t("invalid_date_format"), (value) => {
							if (!value || value.length === 0) return false;
							const firstDate = value[0];
							const secondDate = value[1];
							if (
								!(firstDate instanceof Date) ||
								isNaN(firstDate.getTime()) ||
								!(secondDate instanceof Date) ||
								isNaN(secondDate.getTime())
							) {
								return false;
							}
							return true;
						})
						.test(
							"is-first-date-valid",
							t("date_cant_be_from_past"),
							function (value) {
								if (!value || value.length === 0) return false;
								const firstDate = value[0];
								const today = new Date();
								// Clear the time part for accurate comparison
								today.setHours(0, 0, 0, 0);
								if (firstDate) {
									return firstDate >= today;
								}
								return false;
							},
						);
				}

				return schema.notRequired();
			}),
		loadingTimeStart: Yup.date()
			.typeError(t("value_has_to_be_hour"))
			.nullable()
			.required(t("first_hour_in_range_is_required"))
			.when(
				["loadingDateType", "loadingDate", "loadingDateRange"],
				([loadingDateType, loadingDate, loadingDateRange], schema) => {
					const today = new Date();
					today.setHours(0, 0, 0, 0);

					const currentTime = new Date();
					const currentMinutes = currentTime.getMinutes();

					const currentTimeWithoutMinutes = new Date(currentTime);
					currentTimeWithoutMinutes.setMinutes(0, 0, 0);
					let minTime = new Date(currentTimeWithoutMinutes);

					if (currentMinutes >= 45) {
						minTime = addHours(minTime, 2);
					} else {
						minTime = addHours(minTime, 1);
					}

					if (loadingDateType === "day" && loadingDate) {
						const loadingDay = new Date(loadingDate);
						loadingDay.setHours(0, 0, 0, 0);

						const isToday = today.getTime() === loadingDay.getTime();

						if (isToday) {
							return schema.test({
								name: "is-after-hours-day",
								exclusive: true,
								message: t("earliest_hours_you_can_set_for_load_today_is_n", {
									value: minTime.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									}),
								}),
								test: (loadingTimeStart) => {
									return minTime <= loadingTimeStart;
								},
							});
						}
					}
					if (
						loadingDateType === "range" &&
						loadingDateRange &&
						loadingDateRange.length > 0
					) {
						const loadingDay = new Date(loadingDateRange[0]);
						loadingDay.setHours(0, 0, 0, 0);

						const isToday = today.getTime() === loadingDay.getTime();

						if (isToday) {
							return schema.test({
								name: "is-after-hours-range",
								exclusive: true,
								message: t("earliest_hours_you_can_set_for_load_today_is_n", {
									value: minTime.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									}),
								}),
								test: (loadingTimeStart) => {
									return minTime <= loadingTimeStart;
								},
							});
						}
					}
					return schema;
				},
			),
		loadingTimeEnd: Yup.date()
			.transform((value, originalValue) => {
				// If the user typed an empty string, treat it as null
				return originalValue === "" ? null : value;
			})
			.typeError(t("value_has_to_be_hour"))
			.nullable()
			.notRequired()
			.when("loadingTimeStart", ([loadingTimeStart], schema) => {
				return schema.test({
					name: "is-after-start",
					exclusive: true,
					message: t("second_hour_in_range_has_to_be_greater_than_first"),
					test: (loadingTimeEnd) => {
						const startTime =
							loadingTimeStart &&
							loadingTimeStart.getHours() * 60 + loadingTimeStart.getMinutes();
						const endTime =
							loadingTimeEnd &&
							loadingTimeEnd.getHours() * 60 + loadingTimeEnd.getMinutes();

						return !loadingTimeStart || !loadingTimeEnd || startTime < endTime;
					},
				});
			}),

		unloadingDateType: Yup.string().required(t("select_date_type_unload")),
		unloadingDateRange: Yup.array()
			.of(Yup.date())
			.nullable()
			.when("unloadingDateType", ([unloadingDateType], schema) => {
				if (unloadingDateType === "range") {
					return schema
						.required(t("range_is_required_if_selected_range"))
						.test(
							"is-not-null-or-empty",
							t("range_is_required_if_selected_range"),
							(value) => {
								return (
									value !== null && value !== undefined && value.length > 0
								);
							},
						)
						.test("is-valid-dates", t("invalid_date_format"), (value) => {
							if (!value || value.length === 0) return false;
							const firstDate = value[0];
							const secondDate = value[1];
							if (
								!(firstDate instanceof Date) ||
								isNaN(firstDate.getTime()) ||
								!(secondDate instanceof Date) ||
								isNaN(secondDate.getTime())
							) {
								return false;
							}
							return true;
						});
				}
			})
			.when(
				[
					"loadingDateType",
					"unloadingDateType",
					"loadingDateRange",
					"loadingDate",
				],
				(
					[loadingDateType, unloadingDateType, loadingDateRange, loadingDate],
					schema,
				) => {
					if (unloadingDateType !== "range") return schema;
					if (unloadingDateType === "range") {
						schema = schema.test({
							name: "is-range-empty",
							exclusive: true,
							message: t("range_is_required_if_selected_range"),
							test: (unloadingDateRange) =>
								unloadingDateRange !== null &&
								unloadingDateRange !== undefined &&
								unloadingDateRange.length > 0,
						});
					}
					if (
						loadingDateType === "range" &&
						loadingDateRange &&
						loadingDateRange.length > 1
					) {
						const startLoadingRange = loadingDateRange[0];
						let minUnloadingDate = new Date(startLoadingRange);
						minUnloadingDate.setHours(0, 0, 0, 0);

						let maxUnloadingDate = new Date(startLoadingRange);
						maxUnloadingDate.setHours(0, 0, 0, 0);
						maxUnloadingDate = addDays(maxUnloadingDate, 30);

						schema = schema
							.test({
								name: "is-after-loading",
								exclusive: true,
								message: t(
									"first_unload_date_has_to_be_after_or_same_as_second_load_date",
								),
								test: (unloadingDateRange) =>
									minUnloadingDate <= unloadingDateRange[0],
							})
							.test({
								name: "is-30",
								exclusive: true,
								message: t(
									"second_unload_date_cant_be_after_first_load_date_plus_30_days",
								),
								test: (unloadingDateRange) =>
									maxUnloadingDate >= unloadingDateRange[1],
							});
					}
					if (loadingDateType === "day" && loadingDate) {
						let minUnloadingDate = new Date(loadingDate);
						minUnloadingDate.setHours(0, 0, 0, 0);
						let maxUnloadingDate = new Date(loadingDate);
						maxUnloadingDate.setHours(0, 0, 0, 0);
						maxUnloadingDate = addDays(maxUnloadingDate, 30);

						schema = schema
							.test({
								name: "is-after-loading",
								exclusive: true,
								message: t(
									"first_unload_date_has_to_be_after_or_same_as_unload_date",
								),
								test: (unloadingDateRange) =>
									minUnloadingDate <= unloadingDateRange[0],
							})
							.test({
								name: "is-30",
								exclusive: true,
								message: t(
									"second_unload_date_cant_be_after_unload_date_plus_30_days",
								),
								test: (unloadingDateRange) =>
									maxUnloadingDate >= unloadingDateRange[1],
							});
					}
					return schema;
				},
			),
		unloadingTimeStart: Yup.date()
			.typeError(t("value_has_to_be_hour"))
			.nullable()
			.required(t("first_hour_in_range_is_required"))
			.when(
				[
					"loadingTimeStart",
					"loadingTimeEnd",
					"unloadingDateType",
					"unloadingDate",
					"unloadingDateRange",
					"loadingDateType",
					"loadingDate",
					"loadingDateRange",
				],
				(
					[
						loadingTimeStart,
						loadingTimeEnd,
						unloadingDateType,
						unloadingDate,
						unloadingDateRange,
						loadingDateType,
						loadingDate,
						loadingDateRange,
					],
					schema,
				) => {
					const today = new Date();
					today.setHours(0, 0, 0, 0);

					if (
						loadingDateType === "day" &&
						loadingDate &&
						unloadingDateType === "day" &&
						unloadingDate
					) {
						const loadingDay = new Date(loadingDate);
						loadingDay.setHours(0, 0, 0, 0);
						loadingDay.setSeconds(0);
						loadingDay.setMilliseconds(0);

						const unloadingDay = new Date(unloadingDate);
						unloadingDay.setHours(0, 0, 0, 0);
						unloadingDay.setSeconds(0);
						unloadingDay.setMilliseconds(0);

						const isTheSameDay =
							loadingDay.getTime() === unloadingDay.getTime();

						if (isTheSameDay && loadingTimeStart) {
							let minTime = new Date(loadingTimeStart);
							// if (loadingTimeEnd) {
							// 	minTime = new Date(loadingTimeEnd);
							// }

							minTime = addMinutes(minTime, 30);
							minTime.setSeconds(0);
							minTime.setMilliseconds(0);

							return schema.test({
								name: "unloading-is-after-loading-hours-day",
								exclusive: true,
								message: t("earliest_hours_you_can_set_for_unload_today_is_n", {
									value: minTime.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									}),
								}),
								test: (unloadingTimeStart) => {
									return minTime <= unloadingTimeStart;
								},
							});
						}
					}

					if (
						loadingDateType === "day" &&
						loadingDate &&
						unloadingDateType === "range" &&
						unloadingDateRange &&
						unloadingDateRange.length > 0
					) {
						const loadingDay = new Date(loadingDate);
						loadingDay.setHours(0, 0, 0, 0);
						loadingDay.setSeconds(0);
						loadingDay.setMilliseconds(0);

						const unloadingDay = new Date(unloadingDateRange[0]);
						unloadingDay.setHours(0, 0, 0, 0);
						unloadingDay.setSeconds(0);
						unloadingDay.setMilliseconds(0);

						const isTheSameDay =
							loadingDay.getTime() === unloadingDay.getTime();

						if (isTheSameDay && loadingTimeStart) {
							let minTime = new Date(loadingTimeStart);
							// if (loadingTimeEnd) {
							// 	minTime = new Date(loadingTimeEnd);
							// }

							minTime = addMinutes(minTime, 30);
							minTime.setSeconds(0);
							minTime.setMilliseconds(0);

							return schema.test({
								name: "unloading-is-after-loading-hours-range",
								exclusive: true,
								message: t("earliest_hours_you_can_set_for_unload_is_n", {
									value: minTime.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									}),
								}),
								test: (unloadingTimeStart) => {
									return minTime <= unloadingTimeStart;
								},
							});
						}
					}

					if (
						loadingDateType === "range" &&
						loadingDateRange &&
						loadingDateRange.length > 0 &&
						unloadingDateType === "range" &&
						unloadingDateType &&
						unloadingDateType.length > 0
					) {
						const loadingDay = new Date(loadingDateRange[0]);
						loadingDay.setHours(0, 0, 0, 0);
						loadingDay.setSeconds(0);
						loadingDay.setMilliseconds(0);

						let isTheSameDay = false;
						if (unloadingDateRange?.length > 0) {
							const unloadingDay = new Date(unloadingDateRange[0]);
							unloadingDay.setHours(0, 0, 0, 0);
							unloadingDay.setSeconds(0);
							unloadingDay.setMilliseconds(0);

							isTheSameDay = loadingDay.getTime() === unloadingDay.getTime();
						}

						if (isTheSameDay && loadingTimeStart) {
							let minTime = new Date(loadingTimeStart);
							// if (loadingTimeEnd) {
							// 	minTime = new Date(loadingTimeEnd);
							// }

							minTime = addMinutes(minTime, 30);
							minTime.setSeconds(0);
							minTime.setMilliseconds(0);

							return schema.test({
								name: "unloading-is-after-loading-hours-range",
								exclusive: true,
								message: t("earliest_hours_you_can_set_for_unload_is_n", {
									value: minTime.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									}),
								}),
								test: (unloadingTimeStart) => {
									return minTime <= unloadingTimeStart;
								},
							});
						}
					}

					if (
						loadingDateType === "range" &&
						loadingDateRange &&
						loadingDateRange.length > 0 &&
						unloadingDateType === "day" &&
						unloadingDate
					) {
						const loadingDay = new Date(loadingDateRange[0]);
						loadingDay.setHours(0, 0, 0, 0);
						loadingDay.setSeconds(0);
						loadingDay.setMilliseconds(0);

						const unloadingDay = new Date(unloadingDate);
						unloadingDay.setHours(0, 0, 0, 0);
						unloadingDay.setSeconds(0);
						unloadingDay.setMilliseconds(0);

						const isTheSameDay =
							loadingDay.getTime() === unloadingDay.getTime();

						if (isTheSameDay && loadingTimeStart) {
							let minTime = new Date(loadingTimeStart);
							// if (loadingTimeEnd) {
							// 	minTime = new Date(loadingTimeEnd);
							// }

							minTime = addMinutes(minTime, 30);
							minTime.setSeconds(0);
							minTime.setMilliseconds(0);

							return schema.test({
								name: "unloading-is-after-loading-hours-range",
								exclusive: true,
								message: t("earliest_hours_you_can_set_for_unload_is_n", {
									value: minTime.toLocaleTimeString([], {
										hour: "2-digit",
										minute: "2-digit",
									}),
								}),
								test: (unloadingTimeStart) => {
									return minTime <= unloadingTimeStart;
								},
							});
						}
					}

					return schema;
				},
			),
		unloadingTimeEnd: Yup.date()
			.transform((value, originalValue) => {
				// If the user typed an empty string, treat it as null
				return originalValue === "" ? null : value;
			})
			.typeError(t("value_has_to_be_hour"))
			.nullable()
			.notRequired()
			.when("unloadingTimeStart", ([unloadingTimeStart], schema) => {
				return schema.test({
					name: "is-after-start",
					exclusive: true,
					message: t("second_hour_in_range_has_to_be_greater_than_first"),
					test: (unloadingTimeEnd) => {
						const startTime =
							unloadingTimeStart &&
							unloadingTimeStart.getHours() * 60 +
								unloadingTimeStart.getMinutes();
						const endTime =
							unloadingTimeEnd &&
							unloadingTimeEnd.getHours() * 60 + unloadingTimeEnd.getMinutes();

						return (
							!unloadingTimeStart || !unloadingTimeEnd || startTime < endTime
						);
					},
				});
			}),
	});
};
